import { useTranslation } from '@jobcost-monorepo/i18n';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import {
  ITimesheetsReportGlobalPrint,
  KeyValue,
  floatToHours,
} from '../helpers';
import type { Dayjs } from 'dayjs';
import dayjs from '@jobcost-monorepo/dayjs-config';
import { MarkType } from '@jobcost-monorepo/database/prisma/enums';

interface Props {
  dataList: ITimesheetsReportGlobalPrint[];
  startDate?: string;
  endDate?: string;
}

const TimesheetGlobalPdf = ({ dataList, startDate, endDate }: Props) => {
  if (dataList.length === 0) {
    return <Document></Document>;
  }
  const { t } = useTranslation('timesheets');
  const start = dayjs.utc(startDate);
  const year = dayjs.utc(startDate).year();
  const month = dayjs
    .utc(startDate)
    .format('MMMM')
    .toString()
    .toLocaleUpperCase();
  const end = dayjs.utc(endDate);
  const defaultName = `REPORT_${month}_${year}.pdf`;

  const dates: Dayjs[] = [];
  let currentDate = start;

  while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
    dates.push(currentDate);
    currentDate = currentDate.add(1, 'day');
  }

  const getContentForDateAndJobOrMarkType = (
    jobOrMarkType: KeyValue | MarkType,
    date: Dayjs,
    report: ITimesheetsReportGlobalPrint,
  ) => {
    const timesheetDate = report.data.groupedDate.find((el) =>
      dayjs.utc(el.operationDate).isSame(date, 'day'),
    );
    if (!timesheetDate) return <Text></Text>;

    const totals = timesheetDate.totalsByJobOrMarkType.find((el) =>
      typeof el.jobOrMarkType === 'object' && typeof jobOrMarkType === 'object'
        ? el.jobOrMarkType.id === jobOrMarkType.id
        : el.jobOrMarkType === jobOrMarkType,
    );

    return <Text>{floatToHours(totals?.totalHours)}</Text>;
  };

  const styles = StyleSheet.create({
    page: {
      padding: 5,
      fontSize: 10,
    },
    section: {
      borderLeftWidth: '1px',
      borderTopWidth: '1px',
      borderColor: 'black',
      width: '540rem',
    },
    table: {
      display: 'flex',
      flexDirection: 'column',
      borderLeftWidth: '1px',
      borderTopWidth: '1px',
      borderColor: 'black',
      width: '100%',
    },
    container: {
      width: '100%',
      padding: 5,
    },
    title: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      paddingVertical: 8,
    },
    cellContent: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 8,
      paddingHorizontal: 2,
      flexDirection: 'column',
      fontSize: 7,
    },
    cellContentResource: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      paddingVertical: 8,
      paddingLeft: 10,
      flexDirection: 'column',
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
    },
    cellResource: {
      height: '100%',
      width: '100%',
      borderRightWidth: '1px',
      borderBottomWidth: '1px',
      borderColor: 'black',
    },
    cell: {
      height: '100%',
      width: '150rem',
      borderRightWidth: '1px',
      borderBottomWidth: '1px',
      borderColor: 'black',
    },
    cellTot: {
      height: '100%',
      width: '200rem',
      borderRightWidth: '1px',
      borderBottomWidth: '1px',
      borderColor: 'black',
    },
  });

  return (
    <Document title={defaultName}>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.title}>
          <Text>
            {month} {year}
          </Text>
        </View>
        <View>
          {dataList.map((report) => {
            if (report.print) {
              return (
                <View key={report.data.resource.id} style={styles.container}>
                  <View style={styles.table}>
                    {/* header */}
                    <View style={styles.row}>
                      <View style={styles.cellResource}>
                        <View style={styles.cellContentResource}>
                          <Text>{report.data.resource.name}</Text>
                        </View>
                      </View>
                      {dates.map((date) => {
                        return (
                          <View key={date.daysInMonth()} style={styles.cell}>
                            <View style={styles.cellContent}>
                              <Text>
                                {date.locale('it').format('ddd').toUpperCase()}
                              </Text>
                              <Text>{date.format('DD')}</Text>
                            </View>
                          </View>
                        );
                      })}
                      <View style={styles.cellTot}>
                        <View style={styles.cellContent}>
                          <Text>{t('timesheetReportGlobal.totalShort')}.</Text>
                        </View>
                      </View>
                    </View>
                    {/* content */}
                    {report.data.totalsByJobOrMarkType.map((el, index) => {
                      return (
                        <View style={styles.row} key={index}>
                          <View style={styles.cellResource}>
                            <View style={styles.cellContentResource}>
                              <Text>
                                {typeof el.jobOrMarkType === 'object'
                                  ? el.jobOrMarkType.name
                                  : t(`enum.markType.${el.jobOrMarkType}`)}
                              </Text>
                            </View>
                          </View>

                          {dates.map((date) => (
                            <View key={date.daysInMonth()} style={styles.cell}>
                              <View style={styles.cellContent}>
                                {getContentForDateAndJobOrMarkType(
                                  el.jobOrMarkType,
                                  date,
                                  report,
                                )}
                              </View>
                            </View>
                          ))}
                          <View style={styles.cellTot}>
                            <View style={styles.cellContent}>
                              <Text>
                                {floatToHours(el.totalHours)}{' '}
                                {typeof el.jobOrMarkType !== 'object' &&
                                  t(
                                    `enum.markTypeShort.${el.jobOrMarkType}`,
                                  ).toUpperCase()}
                              </Text>
                            </View>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                </View>
              );
            }
          })}
        </View>
      </Page>
    </Document>
  );
};

export default TimesheetGlobalPdf;
